import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.living';

export default function SeoArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Artigo n&atilde;o encontrado</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Voltar ao In&iacute;cio
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        <header className="relative w-full pt-24 pb-14 md:pt-32 md:pb-18 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 opacity-25">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/60 to-foreground-950"></div>
          <div className="relative max-w-4xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs md:text-sm mb-6" aria-label="Breadcrumb">
              <Link to="/" className="text-white/50 hover:text-white/80 cursor-pointer transition-colors">
                In&iacute;cio
              </Link>
              <span className="text-white/30">/</span>
              <Link to="/" className="text-white/50 hover:text-white/80 cursor-pointer transition-colors">
                Artigos
              </Link>
              <span className="text-white/30">/</span>
              <span className="text-white/80 line-clamp-1">{article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 mb-4">
              SEO Article
            </span>
            <h1 className="text-xl sm:text-2xl md:text-4xl font-heading font-bold text-white leading-tight">
              {article.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/60 max-w-2xl leading-relaxed">
              {article.metaDescription}
            </p>

            <div className="mt-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-white/80 font-semibold text-sm">HS</span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{article.author}</p>
                <p className="text-xs text-white/40">Atualizado em {article.date} &middot; {article.readTime} de leitura</p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-52 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Neste Artigo</h4>
                <nav className="space-y-1.5">
                  {article.sections.map((section, i) => (
                    <a
                      key={i}
                      href={`#section-${i + 1}`}
                      className="block text-sm text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors py-1 leading-snug"
                    >
                      {section.heading}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="space-y-10">
                {article.sections.map((section, i) => (
                  <section key={i} id={`section-${i + 1}`}>
                    <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p, j) => (
                      <p
                        key={j}
                        className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4"
                        dangerouslySetInnerHTML={{ __html: p }}
                      />
                    ))}
                  </section>
                ))}

                <div className="mt-10 p-6 md:p-8 rounded-xl bg-primary-50 border border-primary-100/60 text-center">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-3">
                    Pronto para come&ccedil;ar?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                    Acesse agora a Hoobuy Spreadsheet e descubra os melhores produtos selecionados para voc&ecirc;.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-all"
                    >
                      Explorar Hoobuy Sheet
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3 rounded-full border border-primary-200 text-primary-600 text-sm font-medium hover:bg-primary-100 cursor-pointer whitespace-nowrap transition-all"
                    >
                      &larr; Voltar ao In&iacute;cio
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {article && (
        <>
          <script type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                'headline': article.title,
                'description': article.metaDescription,
                'author': {
                  '@type': 'Person',
                  'name': article.author,
                },
                'publisher': {
                  '@type': 'Organization',
                  'name': 'Hoobuy Sheet',
                  'url': siteUrl,
                },
                'datePublished': '2026-06-01',
                'dateModified': '2026-06-26',
                'url': `${siteUrl}/artigo/${article.slug}`,
                'image': article.imageUrl,
                'inLanguage': 'pt-BR',
                'isAccessibleForFree': true,
                'keywords': article.keywords.join(', '),
                'mainEntityOfPage': {
                  '@type': 'WebPage',
                  '@id': `${siteUrl}/artigo/${article.slug}`,
                },
              }),
            }}
          />
          <script type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                'itemListElement': [
                  {
                    '@type': 'ListItem',
                    'position': 1,
                    'name': 'Início',
                    'item': siteUrl,
                  },
                  {
                    '@type': 'ListItem',
                    'position': 2,
                    'name': 'Artigos',
                    'item': `${siteUrl}/`,
                  },
                  {
                    '@type': 'ListItem',
                    'position': 3,
                    'name': article.title,
                    'item': `${siteUrl}/artigo/${article.slug}`,
                  },
                ],
              }),
            }}
          />
        </>
      )}

      <Footer />
    </main>
  );
}